<template>
  <v-card v-if="item">
    <v-system-bar>{{ item.details.length }} Personen</v-system-bar>

    <v-row
      no-gutters
      align="center"
      class="flex-nowrap"
      v-if="$_personDepartments.includes('BG')"
    >
      <v-col cols="6">
        <v-list-item class="font-weight-medium">
          <v-switch
            v-for="department in $_personDepartments"
            :key="department"
            :disabled="
              item.details.length > 0 ||
              (item && item.status && item.status.code == 'noDetails')
            "
            inset
            label="Sammelrechnung für die FS BG"
            v-model="item.department"
            :value="department"
          ></v-switch>
        </v-list-item>
      </v-col>
    </v-row>

    <v-row no-gutters align="center" class="flex-nowrap">
      <v-col cols="6">
        <v-list-item class="font-weight-medium">
          <v-switch
            :disabled="
              item.details.length > 0 ||
              (item && item.status && item.status.code == 'noDetails') ||
              item.department !== null
            "
            inset
            label="Personen noch unbekannt"
            v-model="item.nodetails"
          ></v-switch>
        </v-list-item>
      </v-col>
      <v-col></v-col>

      <v-col cols="3">
        <CurrencyInput v-model="amountToSet" :currency="currency" />
      </v-col>

      <v-col cols="1">
        <v-btn
          class="mx-2"
          icon
          color="success"
          :small="$vuetify.breakpoint.xs"
          :large="$vuetify.breakpoint.lgAndUp"
          @click="setAmount"
          ><v-icon>mdi-arrow-down-circle-outline</v-icon></v-btn
        >
      </v-col>
    </v-row>
    <v-divider />
    <template v-if="!item.nodetails">
      <v-toolbar flat>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon left v-bind="attrs" v-on="on">mdi-sort</v-icon>
          </template>
          <span
            >Sortieroptionen: gruppiert pro Klasse, nach Name oder nach
            Betrag</span
          >
        </v-tooltip>

        <v-btn-toggle dense v-model="sortOption" mandatory>
          <v-btn text value="schoolClass">Klasse</v-btn>
          <v-btn text value="name">Name</v-btn>
          <v-btn text value="amount" @click="sortDetailsAmount"
            >Betrag</v-btn
          > </v-btn-toggle
        ><v-spacer></v-spacer>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon left v-bind="attrs" v-on="on">mdi-magnify</v-icon>
          </template>
          <span
            >Suche nach Klasse, z.B. "G", oder "27", oder "G27b" und Namen der
            Person</span
          >
        </v-tooltip>

        <v-text-field
          v-model="searchSchoolClass"
          label="Klasse"
          hideDetails
          class="mr-2"
        ></v-text-field>
        <v-text-field
          v-model="searchName"
          label="Name"
          hideDetails
          class="ml-2"
        ></v-text-field>
      </v-toolbar>
      <v-divider />
    </template>

    <v-row
      no-gutters
      v-for="(detail, index) in detailsFiltered"
      :key="index"
      align="center"
      class="flex-nowrap"
    >
      <v-col cols="6">
        <PersonItemBasic
          :dense="$vuetify.breakpoint.xs"
          :value="detail.person"
        ></PersonItemBasic>
      </v-col>
      <v-col cols="2">
        <v-checkbox
          v-if="detail.person.code"
          v-model="detail.expenses"
          label="Spesen"
          color="primary"
        ></v-checkbox>
        <v-checkbox
          v-else-if="item.account.code == 'fieldTrip' && showGYM1"
          v-model="detail.gym1"
          color="info"
          label="GYM1"
        ></v-checkbox>
      </v-col>
      <v-col cols="3">
        <CurrencyInput
          v-model="detail.amountForeign"
          :currency="currency"
          v-if="foreign && !distribute"
        />
        <CurrencyInput v-else v-model="detail.amount" />
      </v-col>
      <v-col cols="1">
        <v-btn
          class="mx-2"
          icon
          color="danger"
          :small="$vuetify.breakpoint.xs"
          :large="$vuetify.breakpoint.lgAndUp"
          @click="removeDetail(detail)"
          ><v-icon>mdi-trash-can</v-icon></v-btn
        >
      </v-col>
    </v-row>

    <v-card-actions>
      <v-btn text disabled> Personen hinzufügen:</v-btn>
      <v-spacer />
      <PersonCoursePicker
        color="success"
        @input="addCourse"
        :disabled="item.nodetails"
      />
      <PersonEventPicker
        color="success"
        @input="addEvent"
        :disabled="item.nodetails"
        v-if="!this.$_isAdminApp"
      />
      <PersonPicker
        color="success"
        @input="addPersons"
        :disabled="item.nodetails"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import { defineComponent } from "vue";
import CurrencyInput from "common/components/CurrencyInput.vue";
import CurrencyValue from "common/components/CurrencyValue.vue";
import PersonItemBasic from "common/components/PersonItemBasic.vue";
import PersonCoursePicker from "common/components/Finance/PersonCoursePicker.vue";
import PersonEventPicker from "common/components/Finance/PersonEventPicker.vue";
import PersonPicker from "common/components/Finance/PersonPicker.vue";

import {
  compareStudentsBySchoolClass,
  comparePeople,
  searchPerson,
} from "common/utils/people.js";
import { createInvoice, foreign } from "../../utils/finance";
import { dateDiff } from "common/utils/date.js";

export default defineComponent({
  components: {
    CurrencyInput,
    CurrencyValue,
    PersonCoursePicker,
    PersonEventPicker,
    PersonItemBasic,
    PersonPicker,
  },
  props: {
    value: {
      type: Object,
      default: createInvoice(),
    },
    distribute: { type: Boolean, default: false },
    page: { type: String, default: "InvoicePending" },
  },
  data() {
    return {
      amountToSet: 0.0,
      events: [],
      item: createInvoice(),
      searchSchoolClass: "",
      searchName: "",
      sortOption: "schoolClass",
      showGYM1: false,
    };
  },
  computed: {
    currency() {
      return this.item.currency ? this.item.currency.code : "CHF";
    },
    detailsFiltered() {
      if (!this.item) {
        return [];
      }

      if (!this.searchSchoolClass && !this.searchName) {
        return this.item.details;
      }

      return this.item.details.filter(
        (el) =>
          (!this.searchSchoolClass ||
            (el.person.schoolClass &&
              el.person.schoolClass.code.includes(this.searchSchoolClass))) &&
          (!this.searchName || searchPerson(el.person, this.searchName))
      );
    },
    lastYear() {
      if (!this.item.schoolYear) return false;
      return dateDiff(this.item.date, this.item.schoolYear.endDate) < 0;
    },
    nextYear() {
      if (!this.item.schoolYear) return false;
      return dateDiff(this.item.date, this.item.schoolYear.startDate) > 0;
    },
    foreign() {
      return foreign(this.item);
    },
    isNew() {
      return !this.id;
    },
  },
  watch: {
    sortOption() {
      this.sortDetails();
      localStorage.setItem("FinanceInvoiceSortOption", this.sortOption);
    },
    "item.department": function (newVal) {
      this.item.nodetails = newVal !== null;
      this.item.grades = { 1: false, 2: false, 3: false, 4: false };
      this.item.currency = { id: 1, local: true };
    },
    "item.schoolYear": function (newVal, oldVal) {
      if (
        oldVal != null &&
        newVal.id != oldVal.id &&
        this.item.details.length > 0
      ) {
        this.showGYM1 = true;
        this.$root.showInfo("Das Jahr hat geändert, GYM1 wurde neu berechnet.");

        this.item.details.forEach((detail) => {
          detail.gym1 = this.lastYear
            ? detail.person.grade &&
              (detail.person.grade.code === "GYM2" ||
                detail.person.grade.code === "GYM1")
            : this.nextYear
            ? false
            : detail.person.grade && detail.person.grade.code === "GYM1";
        });
      }
    },
    item() {
      this.$emit("input", this.item);
    },
    value() {
      this.item = this.value;
      this.showGYM1 = this.lastYear || this.nextYear;
      this.sortDetails();
    },
  },
  methods: {
    addPerson(person) {
      if (!this.hasDetail(person)) {
        // Bei Mitarbeitern und Konto Exkursionen werden per Default Spesen erfasst
        const expenses =
          person.type.code === "teacher" &&
          this.item.account &&
          this.item.account.code === "fieldTrip";

        const gym1 = this.lastYear
          ? person.grade &&
            (person.grade.code === "GYM2" || person.grade.code === "GYM1")
          : this.nextYear
          ? false
          : person.grade && person.grade.code === "GYM1";

        this.item.details.push({
          person: person,
          amount: 0,
          amountForeign: 0,
          expenses: expenses,
          gym1: gym1,
        });
      }
    },
    async addCourse(course) {
      const students = await this.apiList({
        resource: "person/person",
        query: `course=${course.id}`,
      });
      if (!this.item.event) this.item.event = course.title;
      students.forEach((person) => {
        this.addPerson(person);
      });
      this.sortDetails();
    },
    async addEvent(event) {
      const students = event.participants;
      if (!this.item.event) this.item.event = event.description;
      students.forEach((person) => {
        this.addPerson(person);
      });
      this.sortDetails();
    },
    async addPersons(persons) {
      for (const person of persons) {
        this.addPerson(person);
      }
      this.sortDetails();
    },
    hasDetail(person) {
      const details = this.item.details;
      for (let i = 0; i < details.length; ++i) {
        if (details[i].person.id === person.id) return true;
      }
      return false;
    },
    removeDetail(detail) {
      const index = this.item.details.indexOf(detail);
      this.item.details.splice(index, 1);
    },
    setAmount() {
      if (this.foreign) {
        this.item.details.forEach(
          (detail) => (detail.amountForeign = this.amountToSet)
        );
        if (this.item.status && this.item.status.code !== "noDetails") {
          this.item.amountForeign = this.amountToSet * this.item.details.length;
        }
      } else {
        this.item.details.forEach(
          (detail) => (detail.amount = this.amountToSet)
        );
        if (this.item.status && this.item.status.code !== "noDetails") {
          this.item.amount = this.amountToSet * this.item.details.length;
        }
      }
    },
    sortDetails() {
      if (this.sortOption == "schoolClass") {
        this.sortDetailsSchoolclass();
      } else if (this.sortOption == "amount") {
        this.sortDetailsAmount();
      } else {
        this.sortDetailsName();
      }
    },
    sortDetailsAmount() {
      if (this.foreign) {
        this.item.details.sort((a, b) =>
          a.amountForeign > b.amountForeign
            ? 1
            : a.amountForeign < b.amountForeign
            ? -1
            : comparePeople(a.person, b.person)
        );
      } else {
        this.item.details.sort((a, b) =>
          a.amount > b.amount
            ? 1
            : a.amount < b.amount
            ? -1
            : comparePeople(a.person, b.person)
        );
      }
    },
    sortDetailsSchoolclass() {
      this.item.details.sort((a, b) =>
        compareStudentsBySchoolClass(a.person, b.person)
      );
    },
    sortDetailsName() {
      this.item.details.sort((a, b) => comparePeople(a.person, b.person));
    },
  },
  created() {
    this.item = this.value;
    this.sortOption =
      localStorage.getItem("FinanceInvoiceSortOption") || "schoolClass";
  },
});
</script>
